// frontend/src/pages/PowerPlants.jsx
/**
 * ⚡ PAGE CENTRALES SOLAIRES
 *
 * - Un village = une seule ligne (dédoublonnage côté backend via address_key).
 * - Toute modification (puissance, centrale partagée, édition, ajout,
 *   suppression) se reflète immédiatement, sans actualiser la page :
 *   refreshKey fait partie de chaque cacheKey.
 * - Suppression d'un village = définitive : il disparaît de la liste (même
 *   s'il a des compteurs) et ne revient que s'il est ré-ajouté via
 *   « Ajouter ». Les compteurs, clients et transactions ne sont pas touchés.
 */
import React, { useState, useMemo, useEffect } from 'react';
import {
  Zap, TrendingUp, MapPin, Check, X, RefreshCw,
  Search, BarChart3, Pencil, Plus,
  Users, Gauge, Award, Building2, Trash2, Eye,
  FileSpreadsheet, FileText, ChevronsLeft, ChevronsRight,
  ChevronLeft, ChevronRight, Sun, BatteryCharging, Link2, AlertTriangle
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { powerPlantService } from '../services/api';
import { useCachedApi } from '../hooks/useCachedApi';
import { PermissionGuard } from '../components/PermissionGuard';
import { PERMISSIONS } from '../utils/permissions';

const formatNum = (v, digits = 2) =>
  v === null || v === undefined ? '—' : new Intl.NumberFormat('fr-FR', { maximumFractionDigits: digits }).format(v);

const unwrapApi = (result) => {
  if (result == null) return null;
  if (typeof result === 'object' && Object.prototype.hasOwnProperty.call(result, 'data')) {
    return result.data;
  }
  return result;
};

const getApiErrorMessage = (error, fallback = 'Erreur lors de l’enregistrement') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => (typeof item === 'string' ? item : item?.msg || 'Erreur de validation'))
      .join(' — ');
  }
  if (typeof detail === 'object') return detail.msg || detail.message || fallback;
  return fallback;
};

const rowKey = (r) => r.address_key || r.address;

const DATE_SOURCE_LABELS = {
  manuelle: { label: 'Date saisie manuellement', tone: 'text-emerald-600' },
  premiere_transaction: { label: '1ère transaction mesurée (auto)', tone: 'text-blue-500' },
  creation_enregistrement: { label: '⚠️ Date de création (fallback, peu fiable)', tone: 'text-amber-600' },
};

const INSTALLATION_TYPES = [
  { value: 'centrale', label: 'Centrale solaire', icon: Sun, badgeClass: 'bg-amber-50 text-amber-700 border border-amber-200' },
  { value: 'kit_solaire', label: 'Kit solaire', icon: BatteryCharging, badgeClass: 'bg-teal-50 text-teal-700 border border-teal-200' },
];

const getInstallationTypeMeta = (value) => INSTALLATION_TYPES.find((t) => t.value === value) || null;

const CHART_PERIODS = [
  { key: 'week', label: '7 jours' },
  { key: 'month', label: '30 jours' },
  { key: 'year', label: 'Année' },
  { key: 'total', label: 'Total' },
];
const PERIOD_METRIC_LABEL = { week: '7 jours', month: '30 jours', year: 'Année', total: 'Cumulée' };

/* ----------------------------------------------------------------------- */
/* Statistiques                                                             */
/* ----------------------------------------------------------------------- */

const StatCard = ({ title, value, icon: Icon, tint, subtitle }) => (
  <div className="relative bg-white rounded-2xl border border-slate-200 p-5 overflow-hidden">
    <div className={`absolute -right-4 -top-4 w-20 h-20 rounded-full ${tint.bg} opacity-40`} />
    <div className="relative flex items-start justify-between">
      <div>
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{title}</p>
        <p className="text-lg font-semibold text-slate-900 mt-1">{value}</p>
        {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`w-10 h-10 rounded-xl ${tint.bg} flex items-center justify-center shrink-0`}>
        <Icon className={`w-5 h-5 ${tint.text}`} />
      </div>
    </div>
  </div>
);

const SharedPlantsStatCard = ({ sharedCount, sharedList }) => (
  <div
    className="relative bg-white rounded-2xl border border-slate-200 p-5 overflow-hidden"
    title={sharedList && sharedList.length ? sharedList.join('\n') : 'Aucun village rattaché à une autre centrale'}
  >
    <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full bg-indigo-50 opacity-40" />
    <div className="relative flex items-start justify-between">
      <div>
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Centrales partagées</p>
        <p className="text-lg font-semibold text-slate-900 mt-1">{sharedCount ?? '—'}</p>
        <p className="text-xs text-slate-400 mt-1">Villages sans centrale propre, rattachés à celle d'un autre</p>
      </div>
      <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
        <Link2 className="w-5 h-5 text-indigo-500" />
      </div>
    </div>
  </div>
);

const InstallationTypeBadge = ({ type }) => {
  const meta = getInstallationTypeMeta(type);
  if (!meta) return <span className="text-slate-400 text-xs">—</span>;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.badgeClass}`}>
      <Icon size={12} /> {meta.label}
    </span>
  );
};

/* ----------------------------------------------------------------------- */
/* Édition inline de la puissance — "propre" ou "partagée"                  */
/* ----------------------------------------------------------------------- */

const InlinePowerEditor = ({ row, villages, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState(row.shared_with_address ? 'shared' : 'own');
  const [value, setValue] = useState(row.power_kwc || '');
  const [sharedAddress, setSharedAddress] = useState(row.shared_with_display || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const otherVillages = useMemo(
    () => (villages || [])
      .filter((v) => rowKey(v) !== rowKey(row))
      .sort((a, b) => a.address.localeCompare(b.address)),
    [villages, row]
  );

  const startEdit = () => {
    setMode(row.shared_with_address ? 'shared' : 'own');
    setValue(row.power_kwc || '');
    setSharedAddress(row.shared_with_display || '');
    setError('');
    setEditing(true);
  };
  const cancel = () => { setEditing(false); setError(''); };

  const save = async () => {
    if (mode === 'shared') {
      if (!sharedAddress) {
        setError('Choisissez le village qui héberge la centrale');
        return;
      }
    } else {
      const num = Number(value);
      if (!value || isNaN(num) || num <= 0) {
        setError('Doit être > 0');
        return;
      }
    }
    try {
      setSaving(true);
      setError('');
      const common = {
        commune: row.commune || null,
        departement: row.departement || null,
        region: row.region || null,
        has_meters: row.has_meters,
        installation_type: row.installation_type || 'centrale',
        power_kwc: mode === 'shared' ? 0 : Number(value),
        shared_with_address: mode === 'shared' ? sharedAddress : null,
      };
      if (row.has_power_plant) {
        await powerPlantService.update(row.power_plant_id, common);
      } else {
        await powerPlantService.createOrUpdate({ address: row.address, ...common });
      }
      setEditing(false);
      onSaved();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Erreur'));
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && mode === 'own') save();
    if (e.key === 'Escape') cancel();
  };

  if (!editing) {
    return (
      <PermissionGuard permission={PERMISSIONS.MANAGE_POWER_PLANTS}>
        <button
          onClick={startEdit}
          className="group inline-flex items-center gap-2"
          title={row.power_kwc > 0 ? 'Modifier la puissance' : 'Renseigner la puissance ou une centrale partagée'}
        >
          {row.power_kwc > 0 ? (
            <span className="font-semibold text-blue-400">{formatNum(row.power_kwc)} kWc</span>
          ) : row.shared_with_display ? (
            <span className="inline-flex items-center gap-1 text-xs text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-1">
              <Link2 size={11} /> Même centrale que {row.shared_with_display}
            </span>
          ) : (
            <span className="text-xs text-blue-400 bg-white border border-blue-400 rounded-full px-2 py-1">
              Non renseignée
            </span>
          )}
          <Pencil className="w-3.5 h-3.5 text-slate-300 group-hover:text-blue-400 transition-colors" />
        </button>
      </PermissionGuard>
    );
  }

  return (
    <div className="flex flex-col gap-1.5 min-w-[230px]">
      <div className="flex bg-slate-100 rounded-lg p-0.5 text-xs">
        <button
          type="button"
          onClick={() => setMode('own')}
          className={`flex-1 px-2 py-1 rounded-md font-medium transition-colors ${mode === 'own' ? 'bg-white text-blue-400 shadow-sm' : 'text-slate-500'}`}
        >
          Puissance propre
        </button>
        <button
          type="button"
          onClick={() => setMode('shared')}
          className={`flex-1 px-2 py-1 rounded-md font-medium transition-colors ${mode === 'shared' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'}`}
        >
          Centrale partagée
        </button>
      </div>

      {mode === 'own' ? (
        <input
          type="number" step="0.01" autoFocus value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full border border-blue-400 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="kWc"
        />
      ) : (
        <select
          value={sharedAddress}
          onChange={(e) => setSharedAddress(e.target.value)}
          className="w-full border border-indigo-300 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">Le village qui a la centrale...</option>
          {otherVillages.map((v) => (
            <option key={rowKey(v)} value={v.address}>
              {v.address}{v.power_kwc ? ` (${v.power_kwc} kWc)` : ''}
            </option>
          ))}
        </select>
      )}

      <div className="flex items-center gap-1">
        <button onClick={save} disabled={saving} className="p-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg disabled:opacity-50">
          {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
        </button>
        <button onClick={cancel} className="p-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
};

/* ----------------------------------------------------------------------- */
/* Sélecteurs réutilisés dans les modales                                   */
/* ----------------------------------------------------------------------- */

const InstallationTypeSelector = ({ value, onChange }) => (
  <div>
    <label className="text-sm font-medium text-slate-700 block mb-1">Type d'installation *</label>
    <div className="grid grid-cols-2 gap-2">
      {INSTALLATION_TYPES.map((t) => {
        const Icon = t.icon;
        const active = value === t.value;
        return (
          <button
            key={t.value}
            type="button"
            onClick={() => onChange(t.value)}
            className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border-2 text-sm font-medium transition-colors ${
              active ? 'border-blue-400 bg-blue-50 text-blue-500' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
            }`}
          >
            <Icon size={16} /> {t.label}
          </button>
        );
      })}
    </div>
  </div>
);

const SharedOrOwnPowerSelector = ({
  mode, onModeChange, powerKwc, onPowerChange, sharedAddress, onSharedChange, otherVillages,
}) => (
  <div className="space-y-2">
    <div className="flex bg-slate-100 rounded-lg p-1">
      <button
        type="button"
        onClick={() => onModeChange('own')}
        className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${mode === 'own' ? 'bg-white text-blue-400 shadow-sm' : 'text-slate-600'}`}
      >
        Puissance propre
      </button>
      <button
        type="button"
        onClick={() => onModeChange('shared')}
        className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${mode === 'shared' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-600'}`}
      >
        Centrale partagée
      </button>
    </div>
    {mode === 'own' ? (
      <div>
        <label className="text-sm font-medium text-slate-700 block mb-1">Puissance installée (kWc) *</label>
        <input
          type="number" step="0.01" value={powerKwc} onChange={(e) => onPowerChange(e.target.value)}
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Ex: 45.5"
        />
      </div>
    ) : (
      <div>
        <label className="text-sm font-medium text-slate-700 block mb-1">Village qui héberge la centrale *</label>
        <select
          value={sharedAddress}
          onChange={(e) => onSharedChange(e.target.value)}
          className="w-full border border-indigo-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">Sélectionner un village...</option>
          {(otherVillages || []).map((v) => (
            <option key={rowKey(v)} value={v.address}>
              {v.address}{v.power_kwc ? ` (${v.power_kwc} kWc)` : ''}
            </option>
          ))}
        </select>
        <p className="text-xs text-slate-400 mt-1">
          Utilisez ceci quand une centrale alimente plusieurs villages (ex: la centrale
          est installée à Rabia et alimente aussi Ninkidji).
        </p>
      </div>
    )}
  </div>
);

/* ----------------------------------------------------------------------- */
/* Modale d'ajout — y compris ré-ajout d'un village supprimé                */
/* ----------------------------------------------------------------------- */

const AddPowerPlantModal = ({ villages, excludedVillages, onClose, onSaved }) => {
  const [mode, setMode] = useState('existing'); // 'existing' | 'new'
  const [selectedAddress, setSelectedAddress] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [commune, setCommune] = useState('');
  const [departement, setDepartement] = useState('');
  const [region, setRegion] = useState('');
  const [powerMode, setPowerMode] = useState('own');
  const [powerKwc, setPowerKwc] = useState('');
  const [sharedAddress, setSharedAddress] = useState('');
  const [installationType, setInstallationType] = useState('centrale');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const allVillages = useMemo(
    () => [...villages].sort((a, b) => a.address.localeCompare(b.address)),
    [villages]
  );

  const selectedVillage = useMemo(
    () => allVillages.find((v) => v.address === selectedAddress),
    [allVillages, selectedAddress]
  );
  const selectedIsExcluded = useMemo(
    () => !selectedVillage && (excludedVillages || []).some((v) => v.address === selectedAddress),
    [selectedVillage, excludedVillages, selectedAddress]
  );

  const currentAddress = mode === 'existing' ? selectedAddress : newAddress.trim();
  const otherVillages = useMemo(
    () => allVillages.filter((v) => v.address !== currentAddress),
    [allVillages, currentAddress]
  );

  useEffect(() => {
    if (selectedVillage?.has_power_plant) {
      setInstallationType(selectedVillage.installation_type || 'centrale');
      if (selectedVillage.shared_with_address) {
        setPowerMode('shared');
        setSharedAddress(selectedVillage.shared_with_display || '');
        setPowerKwc('');
      } else {
        setPowerMode('own');
        setPowerKwc(selectedVillage.power_kwc ?? '');
        setSharedAddress('');
      }
    } else {
      setPowerKwc('');
      setSharedAddress('');
      setPowerMode('own');
      setInstallationType('centrale');
    }
  }, [selectedVillage]);

  const handleSubmit = async () => {
    const address = currentAddress;
    if (!address) {
      setError(mode === 'existing' ? 'Choisissez un village' : 'Saisissez le nom du village');
      return;
    }
    if (powerMode === 'own' && (!powerKwc || Number(powerKwc) <= 0)) {
      setError('La puissance (kWc) doit être supérieure à 0');
      return;
    }
    if (powerMode === 'shared' && !sharedAddress) {
      setError('Choisissez le village qui héberge la centrale');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await powerPlantService.createOrUpdate({
        address,
        power_kwc: powerMode === 'own' ? Number(powerKwc) : 0,
        shared_with_address: powerMode === 'shared' ? sharedAddress : null,
        commune: (mode === 'existing' ? selectedVillage?.commune : commune) || null,
        departement: (mode === 'existing' ? selectedVillage?.departement : departement) || null,
        region: (mode === 'existing' ? selectedVillage?.region : region) || null,
        installation_type: installationType,
        has_meters: mode === 'existing' ? (selectedVillage?.has_meters ?? true) : false,
      });
      onSaved();
      onClose();
    } catch (e) {
      setError(getApiErrorMessage(e, "Erreur lors de l'enregistrement"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="text-lg font-semibold text-slate-900">Ajouter</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setMode('existing')}
              className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${mode === 'existing' ? 'bg-white text-blue-400 shadow-sm' : 'text-slate-600'}`}
            >
              Village existant
            </button>
            <button
              onClick={() => setMode('new')}
              className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${mode === 'new' ? 'bg-white text-blue-400 shadow-sm' : 'text-slate-600'}`}
            >
              Nouveau village
            </button>
          </div>

          {mode === 'existing' ? (
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Village / Adresse *</label>
              <select
                value={selectedAddress}
                onChange={(e) => setSelectedAddress(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Sélectionner un village... ({allVillages.length} au total)</option>
                <optgroup label="Villages de la liste">
                  {allVillages.map((v) => (
                    <option key={rowKey(v)} value={v.address}>
                      {v.address}
                      {v.commune ? ` — ${v.commune}` : ''}
                      {v.has_power_plant && v.power_kwc ? ` (puissance déjà renseignée : ${v.power_kwc} kWc)` : ''}
                      {v.shared_with_display ? ` (même centrale que ${v.shared_with_display})` : ''}
                      {!v.has_meters ? ' [sans compteur]' : ''}
                    </option>
                  ))}
                </optgroup>
                {excludedVillages?.length > 0 && (
                  <optgroup label="Villages supprimés (à ré-ajouter)">
                    {excludedVillages.map((v) => (
                      <option key={`excl-${v.address_key}`} value={v.address}>
                        {v.address} [supprimé]
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              {allVillages.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">Aucun village trouvé — vérifiez que vos compteurs ont bien une adresse.</p>
              )}

              {selectedIsExcluded && (
                <p className="text-xs text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 mt-3">
                  Ce village avait été supprimé. En l'enregistrant, il réapparaîtra dans la liste avec ses compteurs.
                </p>
              )}

              {selectedVillage && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                  <p className="text-xs font-medium text-blue-500 mb-2">Informations déjà disponibles :</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-white rounded-lg p-2 border border-blue-100">
                      <p className="text-xs text-slate-500">Commune</p>
                      <p className="font-semibold text-slate-900">{selectedVillage.commune || '—'}</p>
                    </div>
                    <div className="bg-white rounded-lg p-2 border border-blue-100">
                      <p className="text-xs text-slate-500">Département</p>
                      <p className="font-semibold text-slate-900">{selectedVillage.departement || '—'}</p>
                    </div>
                    <div className="bg-white rounded-lg p-2 border border-blue-100">
                      <p className="text-xs text-slate-500">Compteurs (actifs/bloqués)</p>
                      <p className="font-semibold text-slate-900">
                        {selectedVillage.active_meters} / {selectedVillage.suspended_meters}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-2 border border-blue-100">
                      <p className="text-xs text-slate-500">Abonnés</p>
                      <p className="font-semibold text-slate-900">{selectedVillage.customer_count}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Nom du village *</label>
                <input
                  type="text" value={newAddress} onChange={(e) => setNewAddress(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Keur Ndiaye"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Commune</label>
                  <input type="text" value={commune} onChange={(e) => setCommune(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Département</label>
                  <input type="text" value={departement} onChange={(e) => setDepartement(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Région</label>
                <input type="text" value={region} onChange={(e) => setRegion(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          )}

          <InstallationTypeSelector value={installationType} onChange={setInstallationType} />

          <SharedOrOwnPowerSelector
            mode={powerMode}
            onModeChange={setPowerMode}
            powerKwc={powerKwc}
            onPowerChange={setPowerKwc}
            sharedAddress={sharedAddress}
            onSharedChange={setSharedAddress}
            otherVillages={otherVillages}
          />

          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium bg-blue-400 hover:bg-blue-500 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

/* ----------------------------------------------------------------------- */
/* Modale d'édition                                                         */
/* ----------------------------------------------------------------------- */

const EditPowerPlantModal = ({ row, villages, onClose, onSaved }) => {
  const [commune, setCommune] = useState(row.commune || '');
  const [departement, setDepartement] = useState(row.departement || '');
  const [region, setRegion] = useState(row.region || '');
  const [powerMode, setPowerMode] = useState(row.shared_with_address ? 'shared' : 'own');
  const [powerKwc, setPowerKwc] = useState(row.power_kwc || '');
  const [sharedAddress, setSharedAddress] = useState(row.shared_with_display || '');
  const [installationType, setInstallationType] = useState(row.installation_type || 'centrale');
  const [installationDate, setInstallationDate] = useState(row.installation_date || '');
  const [notes, setNotes] = useState(row.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const otherVillages = useMemo(
    () => (villages || []).filter((v) => rowKey(v) !== rowKey(row)).sort((a, b) => a.address.localeCompare(b.address)),
    [villages, row]
  );

  const handleSubmit = async () => {
    if (powerMode === 'own' && (!powerKwc || Number(powerKwc) <= 0)) {
      setError('La puissance (kWc) doit être supérieure à 0');
      return;
    }
    if (powerMode === 'shared' && !sharedAddress) {
      setError('Choisissez le village qui héberge la centrale');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = {
        power_kwc: powerMode === 'own' ? Number(powerKwc) : 0,
        shared_with_address: powerMode === 'shared' ? sharedAddress : null,
        commune: commune || null,
        departement: departement || null,
        region: region || null,
        installation_type: installationType,
        installation_date: installationDate || null,
        notes: notes || null,
      };
      if (row.has_power_plant) {
        await powerPlantService.update(row.power_plant_id, payload);
      } else {
        await powerPlantService.createOrUpdate({ address: row.address, has_meters: row.has_meters, ...payload });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(getApiErrorMessage(e, "Erreur lors de l'enregistrement"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="text-lg font-semibold text-slate-900">Modifier « {row.address} »</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Commune</label>
              <input type="text" value={commune} onChange={(e) => setCommune(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Département</label>
              <input type="text" value={departement} onChange={(e) => setDepartement(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">
              Région {!row.region && <span className="text-amber-600 font-normal">(non renseignée — à ajouter)</span>}
            </label>
            <input type="text" value={region} onChange={(e) => setRegion(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Thiès" />
            <p className="text-xs text-slate-400 mt-1">
              Pré-remplie automatiquement à partir d'un contrat déjà généré pour ce village, sinon déduite du département.
            </p>
          </div>

          <InstallationTypeSelector value={installationType} onChange={setInstallationType} />

          <SharedOrOwnPowerSelector
            mode={powerMode}
            onModeChange={setPowerMode}
            powerKwc={powerKwc}
            onPowerChange={setPowerKwc}
            sharedAddress={sharedAddress}
            onSharedChange={setSharedAddress}
            otherVillages={otherVillages}
          />

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Date d'exploitation</label>
            <input type="date" value={installationDate} onChange={(e) => setInstallationDate(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <p className="text-xs text-slate-400 mt-1">
              Si laissée vide, la date de la 1ère transaction (ou de création) est utilisée pour estimer la production théorique.
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium bg-blue-400 hover:bg-blue-500 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

/* ----------------------------------------------------------------------- */
/* Modale de détails                                                        */
/* ----------------------------------------------------------------------- */

const DetailItem = ({ label, value }) => (
  <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
    <p className="text-xs text-slate-500">{label}</p>
    <p className="font-semibold text-slate-900 text-sm mt-0.5">{value ?? '—'}</p>
  </div>
);

const VillageDetailModal = ({ row, onClose }) => {
  const typeMeta = getInstallationTypeMeta(row.installation_type);

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-400" />
            <h3 className="text-lg font-semibold text-slate-900">{row.address}</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <DetailItem label="Commune" value={row.commune} />
            <DetailItem label="Département" value={row.departement} />
            <DetailItem label="Région" value={row.region} />
            <DetailItem label="Type" value={typeMeta ? typeMeta.label : '—'} />
            <DetailItem label="Suivi" value={row.has_meters ? 'Compteurs' : 'Abonnement forfaitaire'} />
            <DetailItem
              label="Puissance centrale"
              value={
                row.power_kwc > 0
                  ? `${formatNum(row.power_kwc)} kWc`
                  : row.shared_with_display
                    ? `Même centrale que ${row.shared_with_display}`
                    : '—'
              }
            />
            <DetailItem label="Date d'exploitation" value={row.installation_date} />
            <DetailItem label="Production max (théorique)" value={row.max_production_kwh ? `${formatNum(row.max_production_kwh, 0)} kWh` : '—'} />
            <DetailItem label="Consommation totale" value={`${formatNum(row.total_production_kwh, 0)} kWh`} />
            <DetailItem label="Ratio (Consommation / Production Max)" value={row.performance_ratio !== null && row.performance_ratio !== undefined ? `${formatNum(row.performance_ratio)}%` : '—'} />
            <DetailItem label="Rendement journalier" value={row.daily_yield_kwh_per_kwc ? `${formatNum(row.daily_yield_kwh_per_kwc, 3)} kWh/kWc/j` : '—'} />
            <DetailItem label="Jours depuis installation" value={row.days_since_installation} />
            {row.installation_date_source && (
              <DetailItem
                label="Source de la date de référence"
                value={
                  <span className={DATE_SOURCE_LABELS[row.installation_date_source]?.tone}>
                    {DATE_SOURCE_LABELS[row.installation_date_source]?.label}
                  </span>
                }
              />
            )}
            <DetailItem label="Revenu total" value={row.total_revenue ? `${formatNum(row.total_revenue, 0)} FCFA` : '—'} />
            <DetailItem label="Compteurs actifs / bloqués" value={`${row.active_meters} / ${row.suspended_meters}`} />
            <DetailItem label="Abonnés" value={row.customer_count} />
          </div>
          {row.notes && (
            <div>
              <p className="text-xs text-slate-500 mb-1">Notes</p>
              <p className="text-sm text-slate-700 bg-slate-50 border border-slate-100 rounded-lg p-3 whitespace-pre-wrap">{row.notes}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

/* ----------------------------------------------------------------------- */
/* Pagination                                                               */
/* ----------------------------------------------------------------------- */

const Pagination = ({ page, totalPages, onChange }) => {
  const pages = useMemo(() => {
    const delta = 1;
    const range = [];
    const rangeWithDots = [];
    let last = null;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= page - delta && i <= page + delta)) {
        range.push(i);
      }
    }
    for (const i of range) {
      if (last !== null) {
        if (i - last === 2) rangeWithDots.push(last + 1);
        else if (i - last > 2) rangeWithDots.push('…');
      }
      rangeWithDots.push(i);
      last = i;
    }
    return rangeWithDots;
  }, [page, totalPages]);

  return (
    <div className="flex items-center gap-1">
      <button onClick={() => onChange(1)} disabled={page <= 1} title="Première page"
        className="p-2 border border-slate-300 rounded-lg disabled:opacity-40 hover:bg-slate-50">
        <ChevronsLeft className="w-4 h-4" />
      </button>
      <button onClick={() => onChange(Math.max(1, page - 1))} disabled={page <= 1}
        className="p-2 border border-slate-300 rounded-lg disabled:opacity-40 hover:bg-slate-50">
        <ChevronLeft className="w-4 h-4" />
      </button>

      {pages.map((p, idx) =>
        p === '…' ? (
          <span key={`dots-${idx}`} className="px-2 text-sm text-slate-400">…</span>
        ) : (
          <button
            key={p}
            onClick={() => onChange(p)}
            className={`min-w-[36px] px-2 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              p === page ? 'bg-blue-400 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {p}
          </button>
        )
      )}

      <button onClick={() => onChange(Math.min(totalPages, page + 1))} disabled={page >= totalPages}
        className="p-2 border border-slate-300 rounded-lg disabled:opacity-40 hover:bg-slate-50">
        <ChevronRight className="w-4 h-4" />
      </button>
      <button onClick={() => onChange(totalPages)} disabled={page >= totalPages} title="Dernière page"
        className="p-2 border border-slate-300 rounded-lg disabled:opacity-40 hover:bg-slate-50">
        <ChevronsRight className="w-4 h-4" />
      </button>
    </div>
  );
};

/* ----------------------------------------------------------------------- */
/* Page principale                                                          */
/* ----------------------------------------------------------------------- */

const PAGE_SIZE = 15;
const CHART_PAGE_SIZE = 200;

const PowerPlants = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [chartPeriod, setChartPeriod] = useState('total');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [deletingRow, setDeletingRow] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [exporting, setExporting] = useState(null);
  // ✅ Villages supprimés à l'instant : masqués immédiatement, avant même
  // que la nouvelle liste revienne du serveur.
  const [removedKeys, setRemovedKeys] = useState(() => new Set());

  const refresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => { setPage(1); }, [search, typeFilter]);

  const listParams = useMemo(() => ({
    page,
    page_size: PAGE_SIZE,
    search: search || undefined,
    installation_type: typeFilter || undefined,
  }), [page, search, typeFilter]);

  // ✅ refreshKey fait partie de chaque cacheKey : après un enregistrement
  // ou une suppression, les données sont rechargées au lieu d'être servies
  // depuis le cache (c'est ce qui obligeait à actualiser la page).
  const { data: plantsRes, loading } = useCachedApi(
    () => powerPlantService.getAll(listParams),
    [refreshKey, listParams],
    { cacheDuration: 15000, cacheKey: `power-plants-list-${refreshKey}-${JSON.stringify(listParams)}` }
  );

  const { data: stats } = useCachedApi(
    () => powerPlantService.getStats(),
    [refreshKey],
    { cacheDuration: 15000, cacheKey: `power-plants-stats-${refreshKey}` }
  );

  const { data: villagesRes } = useCachedApi(
    () => powerPlantService.getVillages(),
    [refreshKey],
    { cacheDuration: 15000, cacheKey: `power-plants-villages-${refreshKey}` }
  );

  const { data: excludedRes } = useCachedApi(
    () => powerPlantService.getExcluded(),
    [refreshKey],
    { cacheDuration: 15000, cacheKey: `power-plants-excluded-${refreshKey}` }
  );

  const chartQueryParams = useMemo(() => {
    const now = new Date();
    if (chartPeriod === 'week') return { days: 7, page_size: CHART_PAGE_SIZE, production_window: true };
    if (chartPeriod === 'month') return { month: now.getMonth() + 1, year: now.getFullYear(), page_size: CHART_PAGE_SIZE, production_window: true };
    if (chartPeriod === 'year') return { year: now.getFullYear(), page_size: CHART_PAGE_SIZE, production_window: true };
    return { page_size: CHART_PAGE_SIZE };
  }, [chartPeriod]);

  const { data: chartRes, loading: loadingChart } = useCachedApi(
    () => powerPlantService.getAll(chartQueryParams),
    [refreshKey, chartPeriod],
    { cacheDuration: 15000, cacheKey: `power-plants-chart-${refreshKey}-${chartPeriod}` }
  );

  const listPayload = unwrapApi(plantsRes) || {};
  const totalPages = listPayload.total_pages || 1;
  const totalItems = listPayload.total_items || 0;

  // ✅ Dédoublonnage de sécurité côté client + masquage immédiat des
  // villages qu'on vient de supprimer.
  const rows = useMemo(() => {
    const seen = new Set();
    return (listPayload.items || []).filter((r) => {
      const k = rowKey(r);
      if (seen.has(k) || removedKeys.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [listPayload.items, removedKeys]);

  // Si la page courante n'existe plus (ex: dernier village de la dernière
  // page supprimé), on se cale sur la page renvoyée par le backend.
  useEffect(() => {
    if (listPayload.page && listPayload.page !== page) setPage(listPayload.page);
  }, [listPayload.page]); // eslint-disable-line react-hooks/exhaustive-deps

  const globalStats = unwrapApi(stats) || {};
  const villages = useMemo(
    () => (unwrapApi(villagesRes) || []).filter((v) => !removedKeys.has(rowKey(v))),
    [villagesRes, removedKeys]
  );
  const excludedVillages = unwrapApi(excludedRes) || [];
  const chartItems = useMemo(
    () => ((unwrapApi(chartRes) || {}).items || []).filter((r) => !removedKeys.has(rowKey(r))),
    [chartRes, removedKeys]
  );

  const avgPerformanceRatio = globalStats.average_performance_ratio ?? null;

  const chartData = useMemo(() => {
    return chartItems
      .filter((r) => r.effective_power_kwc > 0)
      .map((r) => ({
        name: r.address.length > 12 ? `${r.address.slice(0, 12)}…` : r.address,
        fullName: r.shared_with_display ? `${r.address} (centrale : ${r.shared_with_display})` : r.address,
        'Puissance (kWc)': r.effective_power_kwc,
        'Consommation (kWh)': r.total_production_kwh,
        'Production Max (kWh)': r.max_production_kwh,
      }))
      .sort((a, b) => b['Consommation (kWh)'] - a['Consommation (kWh)']);
  }, [chartItems]);

  const periodLabel = PERIOD_METRIC_LABEL[chartPeriod] || '';

  const YieldBadge = ({ value }) => {
    if (value === null || value === undefined) return <span className="text-sm text-slate-400">—</span>;
    const isAbove = avgPerformanceRatio !== null && value >= avgPerformanceRatio;
    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
          isAbove ? 'bg-blue-50 text-blue-400' : 'bg-slate-100 text-slate-600'
        }`}
        title={avgPerformanceRatio !== null ? `Moyenne du parc : ${formatNum(avgPerformanceRatio)}%` : ''}
      >
        {formatNum(value)}%
      </span>
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-sm">
        <p className="font-semibold text-slate-900 mb-1">{payload[0]?.payload?.fullName}</p>
        {payload.map((p) => (
          <p key={p.dataKey} style={{ color: p.color }}>{p.name || p.dataKey} : {formatNum(p.value, 1)}</p>
        ))}
      </div>
    );
  };

  const openDelete = (row) => {
    setDeleteError('');
    setDeletingRow(row);
  };

  const handleDelete = async () => {
    if (!deletingRow) return;
    const key = rowKey(deletingRow);
    try {
      setDeleting(true);
      setDeleteError('');
      await powerPlantService.deleteVillage(deletingRow.address);
      setRemovedKeys((prev) => new Set(prev).add(key));
      setDeletingRow(null);
      setDetailRow(null);
      setEditingRow(null);
      refresh();
    } catch (e) {
      setDeleteError(getApiErrorMessage(e, 'Erreur lors de la suppression'));
    } finally {
      setDeleting(false);
    }
  };

  // Quand on ré-ajoute un village, il ne doit plus être masqué localement.
  const handleSavedFromAdd = () => {
    setRemovedKeys(new Set());
    refresh();
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async (format) => {
    try {
      setExporting(format);
      const response = format === 'xlsx'
        ? await powerPlantService.exportXlsx(search, typeFilter)
        : await powerPlantService.exportPdf(search, typeFilter);
      downloadBlob(response.data, `centrales_solaires.${format}`);
    } catch (e) {
      alert("Erreur lors de l'export");
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-6 p-6 bg-slate-50 min-h-screen">
      {/* En-tête */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Zap className="w-8 h-8" />
              Centrales Solaires et Kits Solaires
            </h1>
            <p className="text-blue-100 mt-2">Vue complète de tous les villages</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              className="flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/25 text-white rounded-lg transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
            <PermissionGuard permission={PERMISSIONS.MANAGE_POWER_PLANTS}>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white text-blue-400 hover:bg-blue-50 rounded-lg font-medium transition-colors"
              >
                <Plus className="w-4 h-4" />
                Ajouter
              </button>
            </PermissionGuard>
          </div>
        </div>
      </div>

      {/* Statistiques — vue d'ensemble du parc */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Vue d'ensemble du parc</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <StatCard title="Villages au total" value={globalStats.total_villages ?? '—'} icon={MapPin}
            tint={{ bg: 'bg-blue-50', text: 'text-blue-400' }} />
          <StatCard title="Centrales" value={globalStats.total_centrales ?? '—'} icon={Sun}
            tint={{ bg: 'bg-amber-50', text: 'text-amber-600' }} subtitle="Centrales solaires classiques" />
          <StatCard title="Kits solaires" value={globalStats.total_kits ?? '—'} icon={BatteryCharging}
            tint={{ bg: 'bg-teal-50', text: 'text-teal-600' }} subtitle="Villages avec kits solaires individuels" />
          <StatCard title="Avec compteurs" value={globalStats.villages_with_meters ?? '—'} icon={Gauge}
            tint={{ bg: 'bg-sky-50', text: 'text-sky-600' }} subtitle="Villages suivis via compteurs" />
          <StatCard title="Abonnement forfaitaire" value={globalStats.villages_direct_subscription ?? '—'} icon={Users}
            tint={{ bg: 'bg-violet-50', text: 'text-violet-600' }} subtitle="Villages sans compteur" />
          <SharedPlantsStatCard sharedCount={globalStats.villages_shared_count} sharedList={globalStats.villages_shared_list} />
        </div>
      </div>

      {/* Statistiques — performance énergétique */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Performance énergétique</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <StatCard
            title="Capacité installée"
            value={`${formatNum(globalStats.total_installed_capacity_kwc)} kWc`}
            icon={Gauge}
            tint={{ bg: 'bg-indigo-50', text: 'text-indigo-600' }}
            subtitle={`moyen = ${formatNum(globalStats.average_power_per_village_kwc)} kWc / village`}
          />
          <StatCard
            title="Ratio"
            value={avgPerformanceRatio !== null ? `${formatNum(avgPerformanceRatio)}%` : '—'}
            icon={TrendingUp}
            tint={{ bg: 'bg-emerald-50', text: 'text-emerald-600' }}
            subtitle="Consommation / Production Max"
          />
          <StatCard
            title="Meilleur rendement"
            value={globalStats.best_village_performance_ratio !== null && globalStats.best_village_performance_ratio !== undefined ? `${formatNum(globalStats.best_village_performance_ratio)}%` : '—'}
            icon={Award}
            tint={{ bg: 'bg-amber-50', text: 'text-amber-600' }}
            subtitle={globalStats.best_village_address || undefined}
          />
          <StatCard
            title="Couverture géographique"
            value={`${globalStats.communes_covered ?? '—'} communes`}
            icon={Building2}
            tint={{ bg: 'bg-sky-50', text: 'text-sky-600' }}
            subtitle={`${globalStats.regions_covered ?? '—'} région(s)`}
          />
        </div>
      </div>

      {/* Graphe */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-400" />
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Puissance vs Consommation par Village</h2>
              <p className="text-sm text-slate-500">Tous les villages avec puissance renseignée — faites défiler pour tout voir</p>
            </div>
          </div>
          <div className="flex bg-slate-100 rounded-lg p-1">
            {CHART_PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setChartPeriod(p.key)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  chartPeriod === p.key ? 'bg-blue-400 text-white shadow-sm' : 'text-slate-600 hover:text-blue-400'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {loadingChart && chartData.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-400" />
          </div>
        ) : chartData.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-10">Aucune centrale avec puissance renseignée pour cette période</p>
        ) : (
          <div className="overflow-x-auto pb-2">
            <div style={{ width: Math.max(900, chartData.length * 70) }}>
              <BarChart
                width={Math.max(900, chartData.length * 70)}
                height={380}
                data={chartData}
                margin={{ top: 5, right: 16, left: 0, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="name" angle={-40} textAnchor="end" interval={0} height={70} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="Puissance (kWc)" name="Puissance (kWc)" fill="#3B82F6" radius={[4, 4, 0, 0]} barSize={14} />
                <Bar dataKey="Production Max (kWh)" name={`Production Max ${periodLabel} (kWh)`} fill="#10B981" radius={[4, 4, 0, 0]} barSize={14} />
                <Bar dataKey="Consommation (kWh)" name={`Consommation ${periodLabel} (kWh)`} fill="#F97316" radius={[4, 4, 0, 0]} barSize={14} />
              </BarChart>
            </div>
          </div>
        )}
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="text-lg font-semibold text-slate-900">{totalItems} village(s) / adresse(s)</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleExport('xlsx')}
              disabled={exporting !== null}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg disabled:opacity-50"
            >
              {exporting === 'xlsx' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Excel
            </button>
            <button
              onClick={() => handleExport('pdf')}
              disabled={exporting !== null}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-red-50 hover:bg-red-100 text-red-700 rounded-lg disabled:opacity-50"
            >
              {exporting === 'pdf' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              PDF
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Rechercher un village, une commune, un département ou une région..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2.5 text-sm border border-slate-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2.5 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 sm:w-56"
          >
            <option value="">Tous les types</option>
            {INSTALLATION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        {loading && !plantsRes ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-400" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Village</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Commune</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Département</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Région</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Puissance Centrale</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700"
                    title="Production théorique cumulée depuis l'installation : PC (kWc) × 5.8 kWh/m²/j × 0.7 × jours écoulés">
                    Production Max théorique (cumulée)
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700"
                    title="Somme de toute l'énergie achetée par ce village depuis le début">
                    Consommation totale (cumulée)
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700"
                    title="Consommation totale / Production Max théorique × 100, entre 0% et 100%">
                    Ratio (%)
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Rendement Journalier</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Compteurs (Actifs/Bloqués)</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Abonnés</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={rowKey(row)} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="text-sm font-medium text-slate-900">{row.address}</span>
                        {!row.has_meters && (
                          <span className="text-[10px] bg-violet-50 text-violet-700 border border-violet-200 rounded-full px-1.5 py-0.5">
                            Forfaitaire
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">{row.commune || '—'}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{row.departement || '—'}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{row.region || '—'}</td>
                    <td className="py-3 px-4 text-sm"><InstallationTypeBadge type={row.installation_type} /></td>
                    <td className="py-3 px-4 text-sm">
                      {/* key : l'éditeur repart des nouvelles valeurs après chaque rechargement */}
                      <InlinePowerEditor
                        key={`${rowKey(row)}-${row.power_kwc ?? ''}-${row.shared_with_address ?? ''}`}
                        row={row}
                        villages={villages}
                        onSaved={refresh}
                      />
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">
                      {row.max_production_kwh !== null ? `${formatNum(row.max_production_kwh, 0)} kWh` : '—'}
                    </td>
                    <td className="py-3 px-4 text-sm font-semibold text-slate-900">{formatNum(row.total_production_kwh, 0)} kWh</td>
                    <td className="py-3 px-4 text-sm"><YieldBadge value={row.performance_ratio} /></td>
                    <td className="py-3 px-4 text-sm text-slate-600">
                      {row.daily_yield_kwh_per_kwc !== null ? `${formatNum(row.daily_yield_kwh_per_kwc, 3)} kWh/kWc/j` : '—'}
                    </td>
                    <td className="py-3 px-4 text-center text-sm">
                      <span className="text-blue-400">{row.active_meters}</span>
                      {' / '}
                      <span className="text-red-600">{row.suspended_meters}</span>
                    </td>
                    <td className="py-3 px-4 text-center text-sm text-slate-700">{row.customer_count}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => setDetailRow(row)}
                          title="Voir les détails"
                          className="p-1.5 text-slate-500 hover:text-blue-400 hover:bg-blue-50 rounded-lg"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <PermissionGuard permission={PERMISSIONS.MANAGE_POWER_PLANTS}>
                          <button
                            onClick={() => setEditingRow(row)}
                            title="Modifier (commune, département, région, type, puissance...)"
                            className="p-1.5 text-slate-500 hover:text-blue-400 hover:bg-blue-50 rounded-lg"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openDelete(row)}
                            title="Supprimer ce village de la liste"
                            className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {rows.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-10">Aucune adresse trouvée</p>
            )}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100 flex-wrap gap-3">
            <p className="text-sm text-slate-500">Page {page} sur {totalPages} ({totalItems} au total)</p>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        )}
      </div>

      {showAddModal && (
        <AddPowerPlantModal
          villages={villages}
          excludedVillages={excludedVillages}
          onClose={() => setShowAddModal(false)}
          onSaved={handleSavedFromAdd}
        />
      )}

      {editingRow && (
        <EditPowerPlantModal
          row={editingRow}
          villages={villages}
          onClose={() => setEditingRow(null)}
          onSaved={refresh}
        />
      )}

      {detailRow && <VillageDetailModal row={detailRow} onClose={() => setDetailRow(null)} />}

      {deletingRow && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <h3 className="text-lg font-semibold text-slate-900">Supprimer ce village ?</h3>
            </div>
            <p className="text-sm text-slate-600 mb-2">
              <strong>{deletingRow.address}</strong> sera retiré définitivement de la liste des centrales,
              avec sa puissance et sa fiche.
            </p>
            <p className="text-xs text-slate-500 mb-5">
              Les compteurs, clients et transactions ne sont pas supprimés. Le village ne réapparaîtra
              que si vous le ré-ajoutez via « Ajouter ».
              {deletingRow.has_power_plant && ' Les villages qui partageaient sa centrale seront détachés.'}
            </p>
            {deleteError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">{deleteError}</p>
            )}
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setDeletingRow(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {deleting && <RefreshCw className="w-4 h-4 animate-spin" />}
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PowerPlants;